import { Button, Divider, Header, Layout, Paragraph, Subheader, TheVoice } from "@obs/base";
import { useSignals } from "@preact/signals-react/runtime";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useIntl } from "react-intl";

import { StationAutocompleteField } from "../Components/Form/Autocomplete/StationAutocompleteField";
import { displayMode, searchParamsSettings } from "../Components/Settings/settingsState";
import type { Station } from "../generated/openapi-codegen/ob-software/station-display/station-display/models/Station";
import { getDisplayModeMessage } from "../intl/enums";

export const Route = createFileRoute("/select")({
	component: RouteComponent,
});

function RouteComponent() {
	useSignals();
	const { formatMessage } = useIntl();
	const navigate = useNavigate();
	const [station, setStation] = useState<Station | null>(null);

	const handleGoToStation = () => {
		if (!station) return;
		navigate({
			to: "/$stationCode",
			params: { stationCode: station.uic_code },
			search: searchParamsSettings.value,
		});
	};

	return (
		<>
			<Header.Web />
			<Layout.Container maxWidth="sm" render={<main />}>
				<TheVoice>
					{formatMessage({
						id: "station_display.select.title",
						defaultMessage: "Find your departures",
						description:
							"Screen-reader title for the universal QR landing page. Passengers land here after scanning a universal QR code.",
					})}
				</TheVoice>
				<Subheader>
					{formatMessage({
						id: "station_display.select.title",
						defaultMessage: "Find your departures",
					})}
				</Subheader>
				<Paragraph>
					{formatMessage({
						id: "station_display.select.description",
						defaultMessage: "Select a station below to view upcoming {displayMode}.",
						description:
							"Helper paragraph on the universal QR landing page. Guides the passenger to pick their station. {displayMode} is replaced by 'Departures', 'Arrivals' or 'Arrivals & Departures'.",
					}, {
						displayMode: formatMessage(getDisplayModeMessage(displayMode.value)).toLowerCase(),
					})}
				</Paragraph>
				<Divider.Section />
				<StationAutocompleteField
					label={formatMessage({
						id: "station_display.select.station.label",
						defaultMessage: "Station",
						description: "Label for the station selector on the universal QR landing page.",
					})}
					placeholder={formatMessage({
						id: "station_display.select.station.placeholder",
						defaultMessage: "Search for a station…",
						description: "Placeholder text for the station search input on the universal QR landing page.",
					})}
					value={station}
					onChange={setStation}
				/>
				<Layout.Box.Default>
					<Button.Primary disabled={!station} onClick={handleGoToStation}>
						{formatMessage(
							{
								id: "station_display.select.button.show",
								defaultMessage: "Show {displayMode}",
								description:
									"CTA button on the universal QR landing page. {displayMode} is replaced by 'Departures', 'Arrivals' or 'Arrivals & Departures'.",
							},
							{ displayMode: formatMessage(getDisplayModeMessage(displayMode.value)) },
						)}
					</Button.Primary>
				</Layout.Box.Default>
			</Layout.Container>
		</>
	);
}
